import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { cartActions } from '../../../store/modules/cart/actions'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { useCart } from '../../cart/contexts/CartContext'
import { useAuth } from '../../auth/contexts/AuthContext'
import { Layout } from '../../../shared/components'
import { PaymentMethod, Address, Battery } from '../../../shared/types/battery'
import { createPaymentIntent } from '../../payment/services/paymentService'
import { couponService, batteryService } from '../../../services'
import { useToast } from '../../../contexts/ToastContext'
import { createLoginRedirectUrl } from '../../../shared/utils/navigationHelpers'
import { CHECKOUT_STORAGE_KEY } from '../../../constants/storageKeys'
// Módulo Checkout - Imports da estrutura modular
import {
  CheckoutSteps,
  OrderSummary,
  RelatedProducts,
  CheckoutStep1,
  CheckoutStep2,
  CheckoutStep3,
  CheckoutNavigationButtons,
  OutOfStockModal
} from '../components'
import {
  useCheckoutCalculations,
  useCheckoutStorage,
  useCheckoutValidation,
  useCheckoutAddress,
  useOrderCreation
} from '../hooks'
import { AppliedCoupon, normalizeCouponData } from '../../../shared/utils/couponHelpers'
import { formatPrice } from '../../../shared/utils/checkoutHelpers'
import { isCampinasRegion } from '../../../constants/checkout'


const CSS_HANDLES = [
  'checkout',
  'checkoutContainer',
  'checkoutContent',
  'checkoutForm',
  'formSection',
  'formTitle',
  'formGroup',
  'formLabel',
  'formInput',
  'formSelect',
  'formTextarea',
  'formRow',
  'paymentMethods',
  'paymentMethod',
  'paymentOption',
  'paymentIcon',
  'paymentInfo',
  'checkoutActions',
  'backButton',
  'continueButton',
  'placeOrderButton',
  'shippingOptions',
  'shippingOption',
  'shippingInfo',
  'shippingPrice',
  'shippingTime'
] as const

const Checkout: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const dispatch = useDispatch()
  const { state: cartState, clearCart, scrapPreference } = useCart()
  // Preferência de sucata por item no carrinho; checkout usa isso nos cálculos (sem checkbox)
  const { addToast } = useToast()
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [appliedCoupons, setAppliedCoupons] = useState<AppliedCoupon[]>([])
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({})
  const [billingAddress, setBillingAddress] = useState<Address>({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Brasil'
  })
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null)
  const [selectedShipping, setSelectedShipping] = useState('standard')
  const [deliveryMethod, setDeliveryMethod] = useState<'shipping' | 'pickup' | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  // Endereço da loja para retirada
  const storeAddress: Address = {
    street: 'Av. José de Sousa',
    number: '2086',
    complement: '',
    neighborhood: 'Nova Campinas',
    city: 'Campinas',
    state: 'SP',
    zipCode: '13025-320',
    country: 'Brasil'
  }
  const [isLoadingPayment, setIsLoadingPayment] = useState(false)
  const [customerInfo, setCustomerInfo] = useState({
    email: '',
    name: '',
    document: ''
  })
  const [paymentProcessed, setPaymentProcessed] = useState(false)
  const [orderId, setOrderId] = useState<string | null>(null)
  const { createOrder, isCreatingOrder, outOfStockItem, clearOutOfStockError } = useOrderCreation()
  const hasAttemptedOrderCreation = useRef(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [showThankYouModal, setShowThankYouModal] = useState(false)
  const [relatedProducts, setRelatedProducts] = useState<Battery[]>([])
  const [isLoadingRelated, setIsLoadingRelated] = useState(false)
  const [relatedImageErrors, setRelatedImageErrors] = useState<Record<string, boolean>>({})
  const hasRestoredData = useRef(false)
  const prevCartIdsRef = useRef('')

  // Gerar fingerprint do carrinho para validar se o pedido ainda é válido
  const cartFingerprint = cartState.items
    .map(item => `${item.product.id}:${item.quantity}`)
    .sort()
    .join('|')
  const [orderCartFingerprint, setOrderCartFingerprint] = useState<string | null>(null)

  // Função para definir o ID do pedido e capturar o fingerprint do carrinho atual
  const updateOrderId = useCallback((id: string | null) => {
    setOrderId(id)
    if (id) {
      setOrderCartFingerprint(cartFingerprint)
    } else {
      setOrderCartFingerprint(null)
    }
  }, [cartFingerprint])

  // Hook para gerenciamento de endereços
  const addressHook = useCheckoutAddress({ isAuthenticated, user, deliveryMethod })
  const {
    shippingAddress,
    setShippingAddress,
    userAddresses,
    isLoadingAddresses,
    selectedAddressId,
    setSelectedAddressId,
    showNewAddressForm,
    setShowNewAddressForm,
    addressConfirmed,
    setAddressConfirmed,
    isLoadingCEP,
    cepError,
    setCepError,
    shippingOptions,
    isLoadingShipping,
    selectedShippingOption,
    setSelectedShippingOption,
    loadAddressToForm,
    calculateShippingOptions,
    handleAddressChange,
    handleCEPBlur,
    saveNewAddress
  } = addressHook

  // Região Campinas = CEP 13xxx ou retirada na loja
  const checkoutZipCode = deliveryMethod === 'pickup' ? storeAddress.zipCode : shippingAddress.zipCode
  const isCampinas = isCampinasRegion(checkoutZipCode)

  // Hook para cálculos (subtotal usa preferência de sucata por item do carrinho)
  const {
    calculateSubtotal,
    calculateShipping,
    calculateDiscount,
    calculateTotal
  } = useCheckoutCalculations({
    appliedCoupons,
    selectedShippingOption,
    shippingOptions,
    isLoadingShipping,
    cartItems: cartState.items,
    isCampinas,
    scrapPreference
  })

  // Hook para storage
  const { isDataLoaded, savedData, saveData, clearData: clearStorageData } = useCheckoutStorage()

  // Hook para validação
  const { validateStep1, validateStep2 } = useCheckoutValidation({
    shippingAddress,
    isAuthenticated,
    hasUser: !!user,
    setFormErrors
  })

  // Carregar dados do localStorage ao montar o componente (apenas uma vez)
  useEffect(() => {
    if (!isDataLoaded || !savedData || hasRestoredData.current) return

    // Restaurar dados salvos apenas na primeira vez
    hasRestoredData.current = true
    if (savedData.shippingAddress) setShippingAddress(savedData.shippingAddress)
    if (savedData.billingAddress) setBillingAddress(savedData.billingAddress)
    if (savedData.customerInfo) setCustomerInfo(savedData.customerInfo)
    if (savedData.selectedPaymentMethod) {
      const validMethods: PaymentMethod[] = [PaymentMethod.PIX, PaymentMethod.CREDIT_CARD, PaymentMethod.DEBIT_CARD, PaymentMethod.BOLETO]
      if (validMethods.includes(savedData.selectedPaymentMethod as PaymentMethod)) {
        setSelectedPaymentMethod(savedData.selectedPaymentMethod as PaymentMethod)
      }
    }
    if (savedData.selectedShipping) setSelectedShipping(savedData.selectedShipping)
    if (savedData.currentStep) setCurrentStep(savedData.currentStep)
    if (savedData.deliveryMethod) setDeliveryMethod(savedData.deliveryMethod)
    if (savedData.orderId) setOrderId(savedData.orderId)
    if (savedData.clientSecret) setClientSecret(savedData.clientSecret)
    if (savedData.cartFingerprint) setOrderCartFingerprint(savedData.cartFingerprint)
    if (Array.isArray(savedData.appliedCoupons)) {
      const restoredCoupons = savedData.appliedCoupons
        .filter((coupon: Partial<AppliedCoupon>) => coupon && typeof coupon.code === 'string')
        .map((coupon: Partial<AppliedCoupon> & { code: string }) => normalizeCouponData(coupon))
      setAppliedCoupons(restoredCoupons)
    }
  }, [isDataLoaded, savedData, scrapPreference])

  // Salvar dados no localStorage sempre que mudarem
  useEffect(() => {
    if (!isDataLoaded) return

    saveData({
      shippingAddress,
      billingAddress,
      customerInfo,
      selectedPaymentMethod,
      selectedShipping,
      currentStep,
      appliedCoupons,
      deliveryMethod,
      orderId,
      clientSecret,
      cartFingerprint: orderCartFingerprint
    })
  }, [
    shippingAddress,
    billingAddress,
    customerInfo,
    selectedPaymentMethod,
    selectedShipping,
    currentStep,
    appliedCoupons,
    deliveryMethod,
    orderId,
    clientSecret,
    orderCartFingerprint,
    isDataLoaded,
    saveData
  ])

  // Resetar erros de imagem quando os itens mudarem
  useEffect(() => {
    setImageErrors({})
    setRelatedImageErrors({})

    // Se o fingerprint do carrinho mudou e já tínhamos um pedido, resetar o pedido
    // para que um novo seja criado com os itens atualizados
    if (orderId) {
      if (!orderCartFingerprint || cartFingerprint !== orderCartFingerprint) {
        console.log('[Checkout] Itens do carrinho mudaram ou fingerprint ausente. Resetando pedido anterior.')
        updateOrderId(null)
        setClientSecret(null)
        hasAttemptedOrderCreation.current = false
      }
    }
  }, [cartState.items, cartFingerprint, orderId, orderCartFingerprint, updateOrderId])

  // Preencher dados do cliente quando usuário autenticar
  useEffect(() => {
    if (isAuthenticated && user) {
      setCustomerInfo({
        email: user.email,
        name: user.name,
        document: user.cpf
      })
    }
  }, [isAuthenticated, user])

  // Atualizar selectedShipping quando selectedShippingOption mudar
  useEffect(() => {
    if (selectedShippingOption) {
      setSelectedShipping(selectedShippingOption)
    }
  }, [selectedShippingOption])

  // Carregar produtos relacionados
  useEffect(() => {
    const loadRelatedProducts = async () => {
      if (cartState.items.length === 0) return

      // Evitar requisições desnecessárias se apenas a quantidade mudou
      const currentIds = cartState.items.map(item => item.product.id).sort().join(',')
      if (currentIds === prevCartIdsRef.current) return
      prevCartIdsRef.current = currentIds

      setIsLoadingRelated(true)
      try {
        // Buscar produtos relacionados baseados nas categorias dos produtos no carrinho
        const cartCategories = cartState.items
          .map(item => item.product.category?.id)
          .filter((id): id is string => Boolean(id))
        const cartProductIds = cartState.items.map(item => item.product.id)

        // Buscar produtos em destaque ou da mesma categoria, excluindo os que já estão no carrinho
        const response = await batteryService.list({
          category: cartCategories.length > 0 ? cartCategories : undefined,
          limit: 6,
          sortBy: 'rating',
          sortOrder: 'desc'
        })

        // Filtrar produtos que já estão no carrinho
        const filtered = response.batteries
          .filter(battery => !cartProductIds.includes(battery.id))
          .slice(0, 6)

        if (filtered.length === 0) {
          // Se não encontrou produtos relacionados, buscar produtos em destaque
          const featuredResponse = await batteryService.getFeatured()
          const featuredFiltered = featuredResponse.batteries
            .filter(battery => !cartProductIds.includes(battery.id))
            .slice(0, 6)
          setRelatedProducts(featuredFiltered)
        } else {
          setRelatedProducts(filtered)
        }
      } catch (error) {
        console.error('Erro ao carregar produtos relacionados:', error)
        // Em caso de erro, tentar buscar produtos em destaque
        try {
          const featuredResponse = await batteryService.getFeatured()
          const cartProductIds = cartState.items.map(item => item.product.id)
          const featuredFiltered = featuredResponse.batteries
            .filter(battery => !cartProductIds.includes(battery.id))
            .slice(0, 6)
          setRelatedProducts(featuredFiltered)
        } catch (featuredError) {
          console.error('Erro ao carregar produtos em destaque:', featuredError)
        }
      } finally {
        setIsLoadingRelated(false)
      }
    }

    loadRelatedProducts()
  }, [cartState.items])

  const handleImageError = (itemId: string) => {
    setImageErrors(prev => ({ ...prev, [itemId]: true }))
  }

  const handleRelatedImageError = (productId: string) => {
    setRelatedImageErrors(prev => ({ ...prev, [productId]: true }))
  }



  const handleApplyCoupon = async (
    couponCode: string
  ): Promise<{ success: boolean; message: string }> => {
    const code = couponCode.trim().toUpperCase()

    if (!code) {
      return {
        success: false,
        message: 'Informe um código de cupom válido.'
      }
    }

    const subtotal = calculateSubtotal()
    if (subtotal <= 0) {
      return {
        success: false,
        message: 'Adicione itens ao carrinho antes de aplicar um cupom.'
      }
    }

    if (appliedCoupons.some(coupon => coupon.code.toUpperCase() === code)) {
      return {
        success: false,
        message: 'Este cupom já foi aplicado.'
      }
    }

    try {
      const validation = await couponService.validate(code, {
        code,
        subtotal
      })

      if (!validation.valid || !validation.coupon) {
        return {
          success: false,
          message: validation.message || 'Cupom inválido.'
        }
      }

      const normalizedCoupon = normalizeCouponData({
        ...validation.coupon,
        discountAmount: validation.discount
      })

      setAppliedCoupons(prev => [...prev, normalizedCoupon])

      return {
        success: true,
        message: validation.message || 'Cupom válido.'
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Erro ao validar o cupom. Tente novamente.'

      return {
        success: false,
        message
      }
    }
  }

  const handleRemoveCoupon = (couponId: string) => {
    setAppliedCoupons(prev => prev.filter(coupon => coupon.id !== couponId))
  }

  // Resetar paymentProcessed quando mudar de método de pagamento ou step
  useEffect(() => {
    setPaymentProcessed(false)
    setClientSecret(null)
    setShowPaymentForm(false)
  }, [selectedPaymentMethod, currentStep])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [])

  // Scroll para o topo quando mudar de step
  useEffect(() => {
    scrollToTop();
  }, [currentStep])

  // Criar Payment Intent quando mostrar o formulário de pagamento (cartão)
  useEffect(() => {
    if (showPaymentForm && (selectedPaymentMethod === PaymentMethod.CREDIT_CARD || selectedPaymentMethod === PaymentMethod.DEBIT_CARD) && !clientSecret) {
      initializeAsaasPayment()
    }
  }, [showPaymentForm, selectedPaymentMethod, clientSecret])

  // Removido useEffect problemático de criação automática de pedido PIX

  const handleStartPayment = async () => {
    if (!selectedPaymentMethod) {
      addToast({
        type: 'error',
        title: 'Método de pagamento',
        message: 'Selecione um método de pagamento.'
      })
      return
    }

    // Verificar valor mínimo (Asaas requer no mínimo R$ 5,00 para Boleto e Pix)
    const total = calculateTotal()
    if (total < 5) {
      addToast({
        type: 'error',
        title: 'Valor Mínimo',
        message: `O valor total do pedido (R$ ${formatPrice(total)}) é inferior ao mínimo permitido de R$ 5,00.`
      })
      return
    }

    setShowPaymentForm(true)

    // Se já tem orderId, não cria novo
    if (orderId) return

    // Criar pedido imediatamente para PIX e Boleto
    if (selectedPaymentMethod === PaymentMethod.PIX || selectedPaymentMethod === PaymentMethod.BOLETO) {
      const newOrderId = await createOrder({
        cartItems: cartState.items,
        shippingAddress,
        billingAddress,
        selectedPaymentMethod,
        deliveryMethod,
        appliedCoupons,
        calculateSubtotal,
        calculateDiscount,
        calculateShipping,
        calculateTotal,
        storeAddress,
        isCampinas,
        scrapPreference
      })
    if (newOrderId) updateOrderId(newOrderId)
    }
    // Para cartão, o pedido é criado dentro de initializeAsaasPayment
  }

  const initializeAsaasPayment = async () => {
    setIsLoadingPayment(true)
    try {
      // Garantir que o pedido existe
      let currentOrderId = orderId
      if (!currentOrderId && selectedPaymentMethod) {
        currentOrderId = await createOrder({
          cartItems: cartState.items,
          shippingAddress,
          billingAddress,
          selectedPaymentMethod,
          deliveryMethod,
          appliedCoupons,
          calculateSubtotal,
          calculateDiscount,
          calculateShipping,
          calculateTotal,
          storeAddress,
          isCampinas,
          scrapPreference
        })
        if (currentOrderId) updateOrderId(currentOrderId)
        if (!currentOrderId) throw new Error('Falha ao criar pedido')
      }

      const response = await createPaymentIntent({
        amount: calculateTotal(),
        description: `Pedido Baterpower Baterias - ${cartState.items.length} item(ns)`,
        metadata: {
          items: cartState.items.map(i => i.product.id).join(','),
          shipping: deliveryMethod === 'pickup' ? 'pickup' : selectedShipping,
          orderId: currentOrderId!
        }
      })
      setClientSecret(response.clientSecret)
    } catch (error) {
      console.error('Erro ao inicializar pagamento:', error)
      addToast({
        type: 'error',
        title: 'Erro no Pagamento',
        message: 'Erro ao inicializar pagamento. Tente novamente.'
      })
    } finally {
      setIsLoadingPayment(false)
    }
  }

  const handlePaymentSuccess = async (paymentId: string) => {
    console.log('Pagamento realizado com sucesso:', paymentId)

    // Se já temos um orderId, apenas redirecionar
    if (orderId) {
      setPaymentProcessed(true)
      addToast({
        type: 'success',
        title: 'Pagamento Confirmado',
        message: 'Redirecionando para seus pedidos...'
      })

      // Limpar carrinho e redirecionar imediatamente
      clearCart()
      localStorage.removeItem(CHECKOUT_STORAGE_KEY)
      navigate('/pedidos')
      return
    }

    // Se não temos orderId, criar o pedido primeiro
    if (selectedPaymentMethod) {
      const newOrderId = await createOrder({
        cartItems: cartState.items,
        shippingAddress,
        billingAddress,
        selectedPaymentMethod,
        deliveryMethod,
        appliedCoupons,
        calculateSubtotal,
        calculateDiscount,
        calculateShipping,
        calculateTotal,
        storeAddress,
        isCampinas,
        scrapPreference
      })
      if (newOrderId) {
        updateOrderId(newOrderId)
        setPaymentProcessed(true)
        addToast({
          type: 'success',
          title: 'Pagamento Confirmado',
          message: 'Redirecionando para seus pedidos...'
        })

        // Limpar carrinho e redirecionar imediatamente
        clearCart()
        localStorage.removeItem(CHECKOUT_STORAGE_KEY)
        navigate('/pedidos')
      }
    }
  }

  const handlePaymentError = (error: string) => {
    console.error('Erro no pagamento:', error)
    setPaymentProcessed(false)
    
    // Se for um erro conhecido de recusa/expiração, dar uma mensagem mais amigável
    let title = 'Erro no Pagamento'
    if (error.toLowerCase().includes('recusada') || error.toLowerCase().includes('autorizada')) {
      title = 'Pagamento Recusado'
    } else if (error.toLowerCase().includes('expirado') || error.toLowerCase().includes('validade')) {
      title = 'Cartão Expirado'
    }

    addToast({
      type: 'error',
      title,
      message: error
    })

    // Scroll para o formulário de pagamento para que o usuário veja o erro detalhado
    const paymentElement = document.getElementById('payment-form-container')
    if (paymentElement) {
      paymentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleContinueToNextStep = async () => {
    // Limpar erros anteriores
    setFormErrors({})

    // Validar step atual antes de avançar
    if (currentStep === 1) {
      if (!validateStep1()) return

      // Verificar autenticação antes de avançar para o pagamento
      if (!isAuthenticated) {
        addToast({
          type: 'info',
          title: 'Login Necessário',
          message: 'Você será redirecionado para fazer login'
        })
        // Redirecionar para login com redirect para checkout
        navigate(createLoginRedirectUrl('/checkout'))
        return
      }

      // Salvar endereço se for novo
      const saved = await saveNewAddress()
      if (!saved) {
        return
      }
    } else if (currentStep === 2) {
      if (!validateStep2()) return

      if (!selectedPaymentMethod) {
        addToast({
          type: 'error',
          title: 'Selecione um método',
          message: 'Escolha um método de pagamento para continuar'
        })
        return
      }
    }

    const nextStep = currentStep + 1
    setCurrentStep(nextStep)

    // Se está indo para o step 3 e PIX está selecionado, criar pedido
    if (nextStep === 3 && selectedPaymentMethod === PaymentMethod.PIX && !orderId && !isCreatingOrder && !hasAttemptedOrderCreation.current) {
      console.log('[Checkout] Avançando para step 3 com PIX selecionado, criando pedido...')
      hasAttemptedOrderCreation.current = true

      const createOrderForPix = async () => {
        const newOrderId = await createOrder({
          cartItems: cartState.items,
          shippingAddress,
          billingAddress,
          selectedPaymentMethod,
          deliveryMethod,
          appliedCoupons,
          calculateSubtotal,
          calculateDiscount,
          calculateShipping,
          calculateTotal,
          storeAddress,
          isCampinas,
          scrapPreference
        })

        if (newOrderId) {
          console.log('[Checkout] Pedido criado com sucesso, orderId:', newOrderId)
          updateOrderId(newOrderId)
        } else {
          console.warn('[Checkout] Falha ao criar pedido para PIX')
          hasAttemptedOrderCreation.current = false
        }
      }

      void createOrderForPix()
    }
  }

  const handlePlaceOrder = () => {
    console.log('Finalizar pedido:', {
      items: cartState.items,
      shippingAddress,
      billingAddress,
      paymentMethod: selectedPaymentMethod,
      shipping: selectedShipping,
      coupons: appliedCoupons,
      total: calculateTotal(),
      paymentProcessed
    })

    // Verificar se o pagamento foi processado
    if (!paymentProcessed || !selectedPaymentMethod) {
      addToast({
        type: 'error',
        title: 'Erro',
        message: 'O pagamento não foi processado corretamente'
      })
      return
    }

    addToast({
      type: 'success',
      title: 'Pedido Finalizado',
      message: 'Pedido criado com sucesso!'
    })

    // Limpar dados do checkout do localStorage
    localStorage.removeItem(CHECKOUT_STORAGE_KEY)

    setTimeout(() => {
      clearCart()
      window.location.href = '/pedidos'
    }, 2000)
  }

  // Função para limpar dados do checkout (pode ser chamada manualmente)
  const clearCheckoutData = () => {
    clearStorageData()
    setShippingAddress({
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Brasil'
    })
    setBillingAddress({
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Brasil'
    })
    setCustomerInfo({
      email: '',
      name: '',
      document: ''
    })
    setSelectedPaymentMethod(null)
    setSelectedShipping('standard')
    setDeliveryMethod(null)
    setCurrentStep(1)
    setAppliedCoupons([])
    setPaymentProcessed(false)
    updateOrderId(null)
    setClientSecret(null)
    setShowPaymentForm(false)
    console.log('Dados do checkout foram limpos')
  }

  const steps = [
    { number: 1, title: 'Endereço', active: currentStep === 1, completed: currentStep > 1 },
    { number: 2, title: 'Pagamento', active: currentStep === 2, completed: currentStep > 2 },
    { number: 3, title: 'Revisão', active: currentStep === 3, completed: currentStep > 3 }
  ]

  // Verificar se o carrinho está vazio
  if (cartState.items.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center">
              <div className="text-6xl mb-4">🛒</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Seu carrinho está vazio
              </h1>
              <p className="text-gray-600 mb-8">
                Adicione alguns produtos ao carrinho antes de finalizar a compra
              </p>
              <button
                onClick={() => dispatch(cartActions.openCart())}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Abrir Carrinho
              </button>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className={`${handles.checkout} min-h-screen bg-gray-50 py-8`}>
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h1 className="text-3xl font-bold text-gray-900">
                Finalizar Compra
              </h1>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={clearCheckoutData}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                  title="Limpar dados salvos e começar do zero"
                >
                  🗑️ Limpar dados
                </button>
              </div>
            </div>
            <p className="text-gray-600">
              Complete seu pedido de forma rápida e segura
            </p>
          </div>

          {/* Steps Indicator */}
          <CheckoutSteps steps={steps} />

          <div className={`${handles.checkoutContainer} grid grid-cols-1 lg:grid-cols-3 gap-8`}>
            {/* Formulário Principal */}
            <div className={`${handles.checkoutContent} lg:col-span-2`}>
              <div className={`${handles.checkoutForm} bg-white rounded-lg shadow-sm p-6`}>

                {/* Step 1: Entrega ou Retirada */}
                {currentStep === 1 && (
                  <CheckoutStep1
                    deliveryMethod={deliveryMethod}
                    setDeliveryMethod={setDeliveryMethod}
                    shippingAddress={shippingAddress}
                    handleAddressChange={handleAddressChange}
                    formErrors={formErrors}
                    setFormErrors={setFormErrors}
                    isLoadingAddresses={isLoadingAddresses}
                    userAddresses={userAddresses}
                    selectedAddressId={selectedAddressId}
                    setSelectedAddressId={setSelectedAddressId}
                    showNewAddressForm={showNewAddressForm}
                    setShowNewAddressForm={setShowNewAddressForm}
                    setShippingAddress={setShippingAddress}
                    addressConfirmed={addressConfirmed}
                    setAddressConfirmed={setAddressConfirmed}
                    selectedShippingOption={selectedShippingOption}
                    setSelectedShippingOption={setSelectedShippingOption}
                    setSelectedShipping={setSelectedShipping}
                    isLoadingCEP={isLoadingCEP}
                    cepError={cepError}
                    setCepError={setCepError}
                    handleCEPBlur={handleCEPBlur}
                    loadAddressToForm={loadAddressToForm}
                    shippingOptions={shippingOptions}
                    isLoadingShipping={isLoadingShipping}
                    calculateShippingOptions={calculateShippingOptions}
                    validateStep1={validateStep1}
                    setCurrentStep={setCurrentStep}
                    formatPrice={formatPrice}
                    handles={handles}
                    storeAddress={storeAddress}
                    saveNewAddress={saveNewAddress}
                  />
                )}

                {/* Step 2: Seleção de Método de Pagamento */}
                {Number(currentStep) === 2 && (
                  <CheckoutStep2
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                    currentStep={currentStep}
                    orderId={orderId}
                    hasAttemptedOrderCreation={hasAttemptedOrderCreation}
                    cartState={cartState}
                    shippingAddress={shippingAddress}
                    billingAddress={billingAddress}
                    deliveryMethod={deliveryMethod}
                    storeAddress={storeAddress}
                    appliedCoupons={appliedCoupons}
                    calculateSubtotal={calculateSubtotal}
                    calculateDiscount={calculateDiscount}
                    calculateShipping={calculateShipping}
                    calculateTotal={calculateTotal}
                    setOrderId={updateOrderId}
                    handles={handles}
                    createOrder={createOrder}
                    isCreatingOrder={isCreatingOrder}
                    isCampinas={isCampinas}
                    scrapPreference={scrapPreference}
                  />
                )}

                {/* Step 3: Revisão */}
                {Number(currentStep) === 3 && (
                  <CheckoutStep3
                    showPaymentForm={showPaymentForm}
                    shippingAddress={shippingAddress}
                    selectedPaymentMethod={selectedPaymentMethod}
                    calculateTotal={calculateTotal}
                    isLoadingPayment={isLoadingPayment}
                    clientSecret={clientSecret}
                    orderId={orderId}
                    isCreatingOrder={isCreatingOrder}
                    hasAttemptedOrderCreation={hasAttemptedOrderCreation}
                    isAuthenticated={isAuthenticated}
                    user={user}
                    handlePaymentSuccess={handlePaymentSuccess}
                    handlePaymentError={handlePaymentError}
                    handles={handles}
                    deliveryMethod={deliveryMethod}
                    storeAddress={storeAddress}
                    onStartPayment={handleStartPayment}
                  />
                )}

                {/* Botões de Navegação */}
                <CheckoutNavigationButtons
                  currentStep={currentStep}
                  showPaymentForm={showPaymentForm}
                  paymentProcessed={paymentProcessed}
                  setCurrentStep={setCurrentStep}
                  setShowPaymentForm={setShowPaymentForm}
                  handleContinueToNextStep={handleContinueToNextStep}
                  handlePlaceOrder={handlePlaceOrder}
                  handles={handles}
                />
              </div>
            </div>

            {/* Resumo do Pedido */}
            <div className="lg:col-span-1">
              <OrderSummary
                currentStep={currentStep}
                appliedCoupons={appliedCoupons}
                onApplyCoupon={handleApplyCoupon}
                onRemoveCoupon={handleRemoveCoupon}
                calculateSubtotal={calculateSubtotal}
                calculateShipping={calculateShipping}
                calculateDiscount={calculateDiscount}
                calculateTotal={calculateTotal}
                shippingAddressZipCode={shippingAddress.zipCode}
                isLoadingShipping={isLoadingShipping}
                imageErrors={imageErrors}
                onImageError={handleImageError}
                deliveryMethod={deliveryMethod}
                isCampinas={isCampinas}
                scrapPreference={scrapPreference}
              />
            </div>
          </div>

          {/* Seção de Produtos Relacionados */}
          <RelatedProducts
            relatedProducts={relatedProducts}
            isLoadingRelated={isLoadingRelated}
            relatedImageErrors={relatedImageErrors}
            onRelatedImageError={handleRelatedImageError}
          />
        </div>
      </div>

      {/* Modal de Estoque Insuficiente */}
      <OutOfStockModal
        isOpen={!!outOfStockItem}
        onClose={clearOutOfStockError}
        outOfStockItem={outOfStockItem}
      />

      {/* Modal de Agradecimento */}
      {showThankYouModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300"
          onClick={() => setShowThankYouModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-8 text-center transform transition-all duration-300 scale-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Obrigado pela sua compra! 🎉
              </h2>
              <p className="text-gray-600">
                Seu pagamento foi confirmado com sucesso. Você receberá um e-mail com os detalhes do seu pedido.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowThankYouModal(false)}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default Checkout
